import { useState, useCallback } from "react";
import {
	getScoringInstructions,
	createScoringInstruction,
	updateScoringInstruction,
	deleteScoringInstruction,
	exportScoringInstructions,
	getImportTemplate,
	importScoringInstructions,
} from "../api/scoring-instructions-api";
import type {
	ScoringInstruction,
	CreateScoringInstructionPayload,
	UpdateScoringInstructionPayload,
} from "../types/ScoringInstruction.type";

interface UseScoringInstructionsResult {
	instructions: ScoringInstruction[];
	isLoading: boolean;
	isSaving: boolean;
	error: string | null;
	fetchInstructions: () => Promise<void>;
	addInstruction: (payload: CreateScoringInstructionPayload) => Promise<ScoringInstruction | null>;
	editInstruction: (uuid: string, payload: UpdateScoringInstructionPayload) => Promise<ScoringInstruction | null>;
	removeInstruction: (uuid: string) => Promise<boolean>;
	handleExport: () => Promise<void>;
	handleDownloadTemplate: () => Promise<void>;
	handleImport: (file: File) => Promise<ScoringInstruction | null>;
}

export const useScoringInstructions = (): UseScoringInstructionsResult => {
	const [instructions, setInstructions] = useState<ScoringInstruction[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const fetchInstructions = useCallback(async (): Promise<void> => {
		setIsLoading(true);
		setError(null);
		try {
			const data = await getScoringInstructions();
			setInstructions(data);
		} catch {
			setError("Failed to load scoring instructions");
		} finally {
			setIsLoading(false);
		}
	}, []);

	const addInstruction = useCallback(
		async (payload: CreateScoringInstructionPayload): Promise<ScoringInstruction | null> => {
			setIsSaving(true);
			setError(null);
			try {
				const created = await createScoringInstruction(payload);
				setInstructions((prev) => [...prev, created]);
				return created;
			} catch {
				setError("Failed to create scoring instruction");
				return null;
			} finally {
				setIsSaving(false);
			}
		},
		[]
	);

	const editInstruction = useCallback(
		async (
			uuid: string,
			payload: UpdateScoringInstructionPayload
		): Promise<ScoringInstruction | null> => {
			setIsSaving(true);
			setError(null);
			try {
				const updated = await updateScoringInstruction(uuid, payload);
				setInstructions((prev) =>
					prev.map((i) => (i.uuid === uuid ? updated : i))
				);
				return updated;
			} catch {
				setError("Failed to update scoring instruction");
				return null;
			} finally {
				setIsSaving(false);
			}
		},
		[]
	);

	const removeInstruction = useCallback(async (uuid: string): Promise<boolean> => {
		setIsSaving(true);
		setError(null);
		try {
			await deleteScoringInstruction(uuid);
			setInstructions((prev) => prev.filter((i) => i.uuid !== uuid));
			return true;
		} catch {
			setError("Failed to delete scoring instruction");
			return false;
		} finally {
			setIsSaving(false);
		}
	}, []);

	const handleExport = useCallback(async (): Promise<void> => {
		setError(null);
		try {
			const blob = await exportScoringInstructions();
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = "scoring-instructions.xlsx";
			link.click();
			window.URL.revokeObjectURL(url);
		} catch {
			setError("Failed to export scoring instructions");
		}
	}, []);

	const handleDownloadTemplate = useCallback(async (): Promise<void> => {
		setError(null);
		try {
			const blob = await getImportTemplate();
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = "scoring-instructions-template.xlsx";
			link.click();
			window.URL.revokeObjectURL(url);
		} catch {
			setError("Failed to download import template");
		}
	}, []);

	const handleImport = useCallback(async (file: File): Promise<ScoringInstruction | null> => {
		setIsSaving(true);
		setError(null);
		try {
			const imported = await importScoringInstructions(file);
			await fetchInstructions();
			return imported;
		} catch {
			setError("Failed to import scoring instructions");
			return null;
		} finally {
			setIsSaving(false);
		}
	}, [fetchInstructions]);

	return {
		instructions,
		isLoading,
		isSaving,
		error,
		fetchInstructions,
		addInstruction,
		editInstruction,
		removeInstruction,
		handleExport,
		handleDownloadTemplate,
		handleImport,
	};
};
